import { Card, CardContent, Chip, colors, Link, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

export const SacraTimestamp = styled(Typography)(({ theme }) => ({
  fontSize: '0.68rem',
  color: theme.palette.text.disabled,
  whiteSpace: 'nowrap',
})) as typeof Typography;

export const OutlinedCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: 'none',
  borderColor: theme.palette.grey[300],
  borderWidth: 2,
}));

type SoftChipColor = 'green' | 'blue' | 'orange' | 'grey';

const SOFT_CHIP_COLORS: Record<SoftChipColor, { bg: string; text: string }> = {
  green:  { bg: colors.green[50],  text: colors.green[800]  },
  blue:   { bg: colors.blue[50],   text: colors.blue[800]   },
  orange: { bg: colors.orange[50], text: colors.orange[800] },
  grey:   { bg: colors.grey[100],  text: colors.grey[700]   },
};

export const CardTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 700,
  color: theme.palette.text.primary,
})) as typeof Typography;

export const SectionHeader = styled(Typography)(({ theme }) => ({
  fontSize: '0.7rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: theme.palette.text.secondary,
  marginBottom: 2,
})) as typeof Typography;

type SourceChipColor = 'blue' | 'orange';

const SOURCE_CHIP_COLORS: Record<SourceChipColor, { border: string; text: string }> = {
  blue:   { border: colors.blue[400],   text: colors.blue[700]   },
  orange: { border: colors.orange[400], text: colors.orange[700] },
};

export const SourceChip = styled(Chip, {
  shouldForwardProp: prop => prop !== 'chipColor',
})<{ chipColor?: SourceChipColor }>(({ chipColor = 'blue' }) => {
  const { border, text } = SOURCE_CHIP_COLORS[chipColor];
  return {
    borderRadius: 4,
    height: 22,
    fontSize: '0.7rem',
    fontWeight: 600,
    backgroundColor: 'transparent',
    color: text,
    border: `1.5px solid ${border}`,
    '& .MuiChip-label': { paddingLeft: 8, paddingRight: 8 },
  };
});

export const SoftChip = styled(Chip, {
  shouldForwardProp: prop => prop !== 'chipColor',
})<{ chipColor?: SoftChipColor }>(({ chipColor = 'blue' }) => {
  const { bg, text } = SOFT_CHIP_COLORS[chipColor];
  return {
    borderRadius: 4,
    height: 22,
    fontSize: '0.72rem',
    fontWeight: 600,
    backgroundColor: bg,
    color: text,
    border: 'none',
    '& .MuiChip-label': { paddingLeft: 8, paddingRight: 8 },
  };
});

export const CardBody = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(2.5),
  '&:last-child': {
    paddingBottom: theme.spacing(2.5),
  },
}));

export const CardTitleRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
}));

// Company Information card — 3-column layout
export const CompanyInfoGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 2fr 1.5fr',
  gap: theme.spacing(4),
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gap: theme.spacing(2),
  },
}));

export const CompanyInfoSection = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.25),
}));

export const TagRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.75),
  marginTop: theme.spacing(0.5),
}));

export const ExecutiveList = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.75),
}));

export const WebsiteLink = styled(Link)(({ theme }) => ({
  fontSize: '0.82rem',
  color: theme.palette.primary.main,
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline' },
}));
